/* eslint-disable @typescript-eslint/no-use-before-define */
import { type ReactNode } from 'react';
import * as React from 'react';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';

import { isInternalLink } from '../../utils';
import { LinkWithQuery } from '../global/LinkWithQuery';

import { CornerCircles, CornerLines, Spinner } from '.';

type TextAlignment = 'left' | 'center' | 'right';
type IconPos = 'left' | 'right';
export type Stylez = 'minimal' | 'default' | 'bold' | 'loud';
type Size = 'small' | 'default' | 'large';

export interface ButtonWrapperProps {
  $active: boolean;
  $disabled: boolean;
  $fill: boolean;
  $multiline: boolean;
  $size: Size;
  $stylez: Stylez;
}

const Height = {
  LARGE: 48,
  DEFAULT: 40,
  SMALL: 36
};

const buttonWrapperStyles = css<ButtonWrapperProps>`
  position: relative;
  flex: none;
  display: flex;
  height: ${({ $size }) =>
    $size === 'small'
      ? `${Height.SMALL}px`
      : $size === 'large'
        ? `${Height.LARGE}px`
        : `${Height.DEFAULT}px`};
  background: transparent;
  outline: none;
  padding: ${({ $size }) => ($size === 'small' ? `0 15px` : `0 20px`)};
  user-select: none;
  cursor: pointer;
  font-size: ${({ $size, theme }) =>
    $size === 'small' ? theme.fontSize.SMALL : theme.fontSize.DEFAULT};
  color: ${({ theme }) => theme.fontColor.DEFAULT};
  border: 1px solid transparent;
  min-width: 0;

  transition:
    background-color ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING},
    border-color ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING};

  ${({ $disabled }) =>
    !$disabled
      ? css`
          &:hover {
            background-color: ${({ theme }) =>
              rgba(theme.borderColor.LOUD, 0.12)};
            border-color: ${({ theme }) => rgba(theme.borderColor.LOUD, 0.4)};
            text-decoration: none;
          }

          &:active {
            background-color: ${({ theme }) =>
              rgba(theme.borderColor.LOUD, 0.2)};
          }
        `
      : css`
          pointer-events: none;
          cursor: default;

          &:hover,
          &:active {
            background-color: transparent;
          }

          .corners {
            display: none;
          }
        `}

  ${({ $fill }) =>
    $fill &&
    css`
      width: 100%;

      ${Inner} {
        width: 100%;
      }
    `}

    ${({ $multiline, $size }) =>
    $multiline &&
    css`
      align-items: center;
      display: flex;
      height: auto;
      min-height: ${$size === 'small'
        ? `${Height.SMALL}px`
        : `${Height.DEFAULT}px`};

      ${Inner},
      ${Children} {
        white-space: unset;
      }
    `}

    ${({ $stylez }) =>
    $stylez === 'minimal' &&
    css`
      border-color: transparent;
    `}

    ${({ $stylez }) =>
    $stylez === 'default' &&
    css`
      border: 1px solid ${({ theme }) => rgba(theme.borderColor.LOUD, 0.2)};
    `}

    ${({ $stylez, theme }) =>
    ($stylez === 'bold' || $stylez === 'loud') &&
    css`
      ${({ theme }) => theme.buttonLoudStyles};
      border: 1px solid transparent;
      background: linear-gradient(
          to right,
          ${theme.backgroundColor},
          ${theme.backgroundColor}
        ),
        linear-gradient(
          45deg,
          ${theme.highlightColor},
          ${theme.colors.LIGHT_PURPLE}
        );
      background-clip: padding-box, border-box;
      background-origin: padding-box, border-box;

      &::after {
        content: '';
        pointer-events: none;
        position: absolute;
        top: 1px;
        left: 1px;
        right: 1px;
        z-index: 1;

        bottom: 1px;
        background-clip: padding-box, border-box;
        background-origin: padding-box, border-box;
        transition: opacity ${theme.animation.DURATION}
          ${theme.animation.EASING};
      }

      &:hover::after {
        opacity: 0.3;
      }

      &:active::after {
        opacity: 0.6;
      }

      [data-theme='pink'] & {
        background-image: linear-gradient(
            to right,
            ${theme.paletteCssVars.fg100},
            ${theme.paletteCssVars.fg100}
          ),
          linear-gradient(
            45deg,
            ${({ theme }) => theme.paletteCssVars.grid700} 0%,
            ${({ theme }) => theme.paletteCssVars.grid700} 100%
          );

        &:hover::after {
          opacity: 0.9;
        }

        &:active::after {
          opacity: 0.7;
        }

        &::after {
          background-color: ${({ theme }) => theme.paletteCssVars.fg1200};
          opacity: 1;

          background-image: linear-gradient(45deg, #373749, #14141c 300%);
        }
      }
    `}

${({ $stylez }) =>
    $stylez === 'loud' &&
    css`
      &:hover {
        div[data-which='corner-circles'] {
          opacity: 0;
        }
      }
    `}
`;

const DivWrapper = styled.div<ButtonWrapperProps>`
  ${buttonWrapperStyles}
`;

const LinkWrapper = styled(LinkWithQuery)<ButtonWrapperProps>`
  ${buttonWrapperStyles}

  &:focus-visible {
    outline: var(--app-focus-outline);
    offset: var(--app-focus-outline-offset);
  }
`;

const Inner = styled.div<{ $alignText: TextAlignment; $iconPos: IconPos }>`
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: row;
  align-items: center;
  height: inherit;
  white-space: nowrap;
  min-width: 0;
  gap: 10px;

  ${({ $iconPos }) =>
    $iconPos === 'right' &&
    css`
      flex-direction: row-reverse;
    `}

  ${({ $alignText }) =>
    $alignText === 'center' &&
    css`
      justify-content: center;
      text-align: center;
    `}

    ${({ $alignText }) =>
    $alignText === 'left' &&
    css`
      justify-content: flex-start;
      text-align: left;
    `}

    ${({ $alignText }) =>
    $alignText === 'right' &&
    css`
      justify-content: flex-end;
      text-align: right;
    `}
`;

const Icon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
`;

const Children = styled.div`
  ${({ theme }) => theme.ellipsis};
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  position: relative;
  z-index: 2;
  pointer-events: none;
`;

export interface ButtonProps
  extends Pick<JSX.IntrinsicElements['a'], 'tabIndex'> {
  active?: boolean;
  alignText?: TextAlignment;
  className?: string;
  disableCornerLines?: boolean;
  disabled?: boolean;
  fill?: boolean;
  icon?: ReactNode;
  iconPos?: IconPos;
  link?: string;
  loading?: boolean;
  multiline?: boolean;
  noreferrer?: boolean;
  onClick?: (evt: React.FormEvent<HTMLDivElement>) => void;
  size?: Size;
  stylez?: Stylez;
  text?: ReactNode;
  trackingLabel?: string;
}

// Minimal: no border, no corners on hover, no circles
// Default: light border, corners on hover, no circles
// Bold: bright border, corners on hover, no circles
// Loud: bright border, corners on hover, circles

export const Button: React.ComponentType<ButtonProps> = React.forwardRef(
  (
    {
      active = false,
      alignText = 'center',
      link,
      text,
      className,
      disabled = false,
      fill = false,
      multiline = false,
      noreferrer = true,
      icon,
      iconPos = 'left',
      onClick,
      loading = false,
      size = 'default',
      stylez = 'default',
      trackingLabel,
      tabIndex,
      disableCornerLines
    },
    ref
  ) => {
    const buttonProps: ButtonWrapperProps = {
      $active: active,
      $disabled: disabled || loading,
      $fill: fill,
      $multiline: multiline,
      $size: size,
      $stylez: stylez
    };

    const content = (
      // @ts-expect-error todo
      <Inner ref={ref} $alignText={alignText} $iconPos={iconPos}>
        {loading && <Spinner />}
        {stylez === 'minimal' || disableCornerLines ? null : (
          <CornerLines
            animateTo={5}
            bottomLeft={true}
            bottomRight={true}
            position={-1}
            size={15}
            topLeft={true}
            topRight={true}
          />
        )}
        {stylez === 'loud' && <CornerCircles />}
        {icon && <Icon>{icon}</Icon>}
        {text && <Children>{text}</Children>}
      </Inner>
    );

    // If onClick, return a normal div
    if (onClick) {
      return (
        <DivWrapper
          {...buttonProps}
          // @ts-expect-error todo
          ref={ref}
          className={className}
          tabIndex={tabIndex}
          onClick={onClick}>
          {content}
        </DivWrapper>
      );
    }

    // Is external link?
    if (link && !isInternalLink(link)) {
      return (
        <LinkWrapper
          // @ts-expect-error todo
          ref={ref}
          {...buttonProps}
          className={className}
          isExternal={true}
          noreferrer={noreferrer}
          tabIndex={tabIndex}
          to={link}
          trackingLabel={trackingLabel}>
          {content}
        </LinkWrapper>
      );
    }

    // Else return a link
    return (
      <LinkWrapper
        {...buttonProps}
        // @ts-expect-error todo
        ref={ref}
        className={className}
        noreferrer={noreferrer}
        tabIndex={tabIndex}
        to={link || ''}
        trackingLabel={trackingLabel}>
        {content}
      </LinkWrapper>
    );
  }
);
